import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Sun, Moon, HelpCircle, Download, Info, Smartphone } from 'lucide-react';

export default function Configuracoes() {
  const { token, user } = useAuth();
  const [theme, setTheme] = useState<'light'|'dark'>('light');
  const [swUpdate, setSwUpdate] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('tialeila_theme') as 'light'|'dark' | null;
    if (saved) setTheme(saved);
    document.documentElement.classList.toggle('dark', theme === 'dark');
    // detecta se já está instalado (modo standalone)
    if (window.matchMedia('(display-mode: standalone)').matches) setInstalled(true);
  }, [theme]);

  const registerSW = async () => {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.register('/sw.js');
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) setSwUpdate(true);
        });
      });
    }
  };
  useEffect(() => { registerSW(); }, []);

  const updateApp = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
      window.location.reload();
    }
  };

  const pwaInstallPrompt = useRef<any>(null);
  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      pwaInstallPrompt.current = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const installPWA = async () => {
    if (pwaInstallPrompt.current) {
      pwaInstallPrompt.current.prompt();
      const { outcome } = await pwaInstallPrompt.current.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      pwaInstallPrompt.current = null;
    } else if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
        <p className="text-gray-600">Preferências, tema e instalação do aplicativo</p>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Aparência</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="radio" name="theme" value="light" checked={theme === 'light'} onChange={() => { setTheme('light'); localStorage.setItem('tialeila_theme', 'light'); document.documentElement.classList.remove('dark'); }} /><Sun size={16} /> Claro
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="radio" name="theme" value="dark" checked={theme === 'dark'} onChange={() => { setTheme('dark'); localStorage.setItem('tialeila_theme', 'dark'); document.documentElement.classList.add('dark'); }} /><Moon size={16} /> Escuro
          </label>
        </div>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Instalar aplicativo (PWA)</h2>
        <p className="text-sm text-gray-600 mb-3">Instale o TiaLeila1 na tela inicial para usar como um aplicativo nativo, também offline.</p>
        {installed ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm"><Info size={16} /> Aplicativo já instalado.</div>
        ) : (
          <button onClick={installPWA} className="btn-primary w-full md:w-auto"><Download size={16} /> Instalar TiaLeila1</button>
        )}
        {swUpdate && (
          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm flex items-center gap-2">
            <Download size={16} /> Atualização disponível. <button onClick={updateApp} className="text-blue-700 underline">Atualizar agora</button>
          </div>
        )}
        <div className="mt-4 text-xs text-gray-500 space-y-1">
          <div><Smartphone size={14} className="mr-1" /> iPhone/iPad: abra no Safari, toque em <b>Compartilhar</b> e escolha <b>Adicionar à Tela de Início</b>.</div>
          <div>Android: toque em <b>Instalar aplicativo</b> quando aparecido, ou no menu do Chrome <b>Adicionar à tela inicial</b>.</div>
        </div>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Sobre o sistema</h2>
        <div className="space-y-1 text-sm text-gray-600">
          <div><b>Tia Leila 1</b> — Sistema de Vendas de Bebidas</div>
          <div>Domínio: https://tialeila1.amadorzona.com</div>
          <div>Usuário admin: admin@tialeila1.local</div>
          <div>Versão 1.0.0 · PWA instalável</div>
        </div>
      </div>
    </div>
  );
}
